import React, { useEffect, useRef, useState } from 'react'
import { Animated, Image, Platform, TouchableHighlight } from 'react-native'
import { atlTheme } from '../../atlTheme'
import { TabsBottom } from './TabsBottom'
import { NavigationStackScreenComponent, StackHeaderTitleProps } from 'react-navigation-stack'
import {
  ATLAtomIcon,
  ATLButton,
  ATLDivider,
  ATLSpacing,
  ATLTextHead,
  ButtonTypeEnum,
  FontWeightEnum,
  SpacingSizesEnum,
} from '@appsimples/atlantica-react-native'
import { icons, images } from '../../constants/searchAssets'
import { screenHeight } from '../../constants/dimensions'
import { formatMessage } from '../../constants/strings/intl/intlHooks'
import { messages } from '../../constants/strings/messages'
import styled from 'styled-components/native'
import { useGlobal } from '../../global/useGlobal'
import { Coordinator } from '../../navigation/coordinator/coordinator'
import { usePromise } from '../../utils/hooks/usePromise'
import { getNotificationsAmount } from '../../services/main/getNotificationsAmount'
import { DividerTypesEnum } from '@appsimples/atlantica-react-native/build/src/molecules/ATLDivider'
import { DashboardCustomDropdown } from './DashboardCustomDropdown'
import { DashboardCustomDropdownItem, DashboardCustomDropdownList } from './DashboardCustomDropdownList'
import { getUserSaleContracts } from '../../services/main/getUserSaleContracts'
import { logout } from '../../utils/logout'
import { syncUser } from '../../services/main/syncUser'
import { Loading } from '../../components/loading/Loading'
import { platformConfig } from '../../constants/platform'
import { CustomCarousel } from '../../components/customCarousel/CustomCarousel'
import { listUserSaleContracts_listUserSaleContracts } from '../../services/types/listUserSaleContracts'
import { useIsFocused } from '@react-navigation/native'
import { changeShouldRefreshNotifications } from '../../services/main/changeShouldRefreshNotifications'

interface CarouselImage {
  id: string
  image: { uri: string }
}

export const MIN_CAROUSEL_HEIGHT = 180

const DashboardContainer = styled.ScrollView`
  background-color: ${atlTheme.ColorBackground};
  flex: 1;
  z-index: 1;
`

const DropdownContainer = styled.View`
  background-color: ${atlTheme.ColorSurface};
  margin-top: ${atlTheme.SpacingSemiAtomic}px;
  margin-bottom: ${atlTheme.SpacingSemiAtomic}px;
`
const NotificationContainer = styled.View`
  background-color: ${atlTheme.ColorSurface};
  border-radius: 10px;
  padding-bottom: ${atlTheme.SpacingBase}px;
`
const Row = styled.View`
  flex-direction: row;
  padding: ${atlTheme.SpacingBase}px ${atlTheme.SpacingMarginal}px;
`

const EnterpriseNameWrapper = styled.View`
  display: flex;
  flex: 1;
  justify-content: center;
`

const CustomDashboardHeader: React.FunctionComponent<StackHeaderTitleProps> = () => {
  const platformBasedMarginLeft = Platform.OS !== 'ios' ? atlTheme.SpacingIntersection + atlTheme.SpacingBase : 0

  return (
    <Image
      source={images.plainLogo}
      style={{
        height: atlTheme.SizeIconBase,
        alignSelf: 'center',
        marginLeft: platformBasedMarginLeft,
      }}
    />
  )
}

export const Dashboard: NavigationStackScreenComponent = () => {
  const [isListOpen, setIsListOpen] = useState<boolean>(false)
  const [selectedItem, setSelectedItem] = useGlobal('selectedUserSaleContract')
  const [dropdownUserSaleContractsData] = useGlobal('dropdownUserSaleContractsData')
  const [userSaleContracts] = useGlobal('userSaleContracts')
  const height = useRef(new Animated.Value(0)).current
  const finalHeight = isListOpen ? 0 : 0.3 * screenHeight
  const transitionDuration = 500
  const timeout = isListOpen ? transitionDuration : 0

  const toggleListVisibility = () => {
    Animated.timing(height, {
      toValue: finalHeight,
      duration: transitionDuration,
      useNativeDriver: false,
    }).start()

    setTimeout(() => {
      setIsListOpen(!isListOpen)
    }, timeout)
  }
  const onSelectDropdownItem = async (selectedItem: DashboardCustomDropdownItem) => {
    await setSelectedItem(selectedItem)
    toggleListVisibility()
  }

  const carouselHeight = Math.max(screenHeight / 4, MIN_CAROUSEL_HEIGHT) - 2 * atlTheme.SpacingMarginal
  const [howManyNotifications] = useGlobal('howManyNotifications')
  const [shouldRefreshNotifications] = useGlobal('shouldRefreshNotifications')
  const areThereNotifications = howManyNotifications > 0

  const { request: fetchHowManyNotifications, loading: isFetchingNotifications } = usePromise(
    getNotificationsAmount,
    [],
    {
      onSuccess: () => changeShouldRefreshNotifications(false),
    }
  )
  const { request: getConstructions } = usePromise(getUserSaleContracts)
  // const { request: sync, loading: isSyncing } = usePromise(syncUser)
  const { request: sync, loading: isSyncing } = usePromise(syncUser)
  const isFocused = useIsFocused()

  useEffect(() => {
    if (isFocused && shouldRefreshNotifications) {
      fetchHowManyNotifications()
    }
  }, [isFocused])

  useEffect(() => {
    getConstructions()
    fetchHowManyNotifications()
    // sync()
  }, [])

  useEffect(() => {
    dropdownUserSaleContractsData && setSelectedItem(dropdownUserSaleContractsData[0])
  }, [dropdownUserSaleContractsData])

  const [carouselImg, setCarouselImg] = useState<CarouselImage[]>()

  useEffect(() => {
    const currentUserSaleContract =
      userSaleContracts && (userSaleContracts[selectedItem?.id] as listUserSaleContracts_listUserSaleContracts)
    const imagesToCarousel = currentUserSaleContract?.construction?.dashboardMedia?.map(saleContract => {
      const { id, url: uri } = saleContract
      return {
        id,
        image: { uri },
      }
    })
    setCarouselImg(imagesToCarousel)
  }, [selectedItem])

  const notificationsText = areThereNotifications
    ? `${formatMessage(messages.dashboard_notifications, { notificationCount: Math.min(howManyNotifications, 10) })}`
    : formatMessage(messages.dashboard_emptyNotificationsList)

  return isFetchingNotifications ? (
    <Loading />
  ) : (
    <>
      <DashboardContainer>
        <DropdownContainer>
          <DashboardCustomDropdown handleClick={toggleListVisibility} selectedItem={selectedItem} />
        </DropdownContainer>
        <CustomCarousel images={carouselImg} carouselHeight={carouselHeight} />
        <ATLSpacing type={SpacingSizesEnum.base} />
        <NotificationContainer>
          <Row>
            <ATLAtomIcon
              source={areThereNotifications ? icons.notifications : icons.noNotifications}
              tintColor={atlTheme.ColorBrand2Default}
            />
            <EnterpriseNameWrapper>
              <ATLTextHead>{notificationsText}</ATLTextHead>
            </EnterpriseNameWrapper>
          </Row>
          <ATLDivider dividerBaseProps={{ color: atlTheme.ColorBackground }} type={DividerTypesEnum.leftSpacing} />
          <ATLButton
            type={ButtonTypeEnum.label}
            title={formatMessage(messages.dashboard_moreNotifications)}
            buttonBaseProps={{
              fontWeight: FontWeightEnum.normal,
              textColorComplementary: atlTheme.ColorBrand2Default,
            }}
            onPress={Coordinator.goToNotificationsScreen}
          />
        </NotificationContainer>
        <ATLSpacing type={SpacingSizesEnum.base} />
        <TabsBottom />
        <ATLSpacing type={SpacingSizesEnum.wide} />
      </DashboardContainer>
      {isListOpen && (
        <DashboardCustomDropdownList
          handleClick={toggleListVisibility}
          height={height}
          onSelect={onSelectDropdownItem}
          data={dropdownUserSaleContractsData}
        />
      )}
    </>
  )
}

const headerLeftWebApp = () => (
  <TouchableHighlight underlayColor={'transparent'} style={{ marginLeft: atlTheme.SpacingMarginal }}>
    <ATLAtomIcon source={icons.mainLogo} size={atlTheme.SizeIconLogoWebApp} />
  </TouchableHighlight>
)

Dashboard.navigationOptions = {
  headerLeft: platformConfig.isWeb() ? () => headerLeftWebApp() : null,
  headerRight: () => (
    <TouchableHighlight
      onPress={logout}
      underlayColor={'transparent'}
      style={{ marginRight: atlTheme.SpacingMarginal }}
    >
      <ATLAtomIcon source={icons.logout} size={atlTheme.SizeIconBase} />
    </TouchableHighlight>
  ),
  headerStyle: {
    height: Platform.OS === 'ios' ? 104 : 74,
    borderBottomWidth: 1,
  },
  headerTitle: props => <CustomDashboardHeader {...props} />,
}
